
import {
  defineComponent, onMounted, ref, computed, watch, onBeforeUnmount,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import RefreshPull from 'pull-refresh-vue3';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '@/store/mainStore';
// eslint-disable-next-line import/no-cycle
import { useDriveStore } from '../store/driveStore';
import ItemCase from '../components/items/ItemCase.vue';
import AppSkeletonCase from '@/components/skeletons/AppSkeletonCase.vue';
// eslint-disable-next-line import/no-cycle
import MonitoringService from '@/services/MonitoringService';
import UsePagination from '@/composables/UsePagination';
import {
  ICase,
} from '@/types/interfaces';
// eslint-disable-next-line import/no-cycle
import DriveService from '../services/DriveService';
import { DIRECTION } from '@/helpers/isScrollEnd';
import UsePullToRefresh from '@/composables/UsePullToRefresh';
import AppNoMessage from '@/components/AppNoMessage.vue';
import { NAVIGATION_PAGES } from '@/types/enums';

export default defineComponent({
  components: {
    ItemCase,
    AppSkeletonCase,
    RefreshPull,
    AppNoMessage,
  },
  setup() {
    const mainStore = useMainStore();
    const driveStore = useDriveStore();
    const router = useRouter();
    const route = useRoute();

    const dataLoading = ref(false);

    const {
      scroller, pageIdx, isPageLoading, onScroll, setLastPage, onCacheScroll,
    } = UsePagination();

    const isPublicCases = computed(() => route.meta.isPublicCases);
    const currentUser = computed(() => mainStore.user.accountId);
    const currentPage = computed(() => route.meta.navigationPage);

    const cases = computed<ICase[]>(() => {
      if (driveStore.search) {
        return driveStore.searchCases;
      }
      return (isPublicCases.value ? driveStore.publicCases.data : driveStore.userPublicCases.data);
    });

    const casesLastPage = computed(() => (isPublicCases.value ? driveStore.publicCases.lastPage : driveStore.userPublicCases.lastPage));
    const casesCurrentPage = computed(() => (isPublicCases.value ? driveStore.publicCases.page : driveStore.userPublicCases.page));

    const noCasesMessage = computed(() => {
      if (!dataLoading.value && !cases.value.length) {
        if (driveStore.search) {
          return {
            text: 'No search results found',
            icon: 'icon-not-found',
          };
        }
        return {
          text: isPublicCases.value ? 'Nothing shared with you yet...' : 'No cases here yet...',
          icon: 'icon-no-data',
        };
      }
      return '';
    });

    function onCaseClick(iCase: ICase) {
      if (driveStore.isCaseSelector) {
        return;
      }

      driveStore.currentCase.title = iCase.title;
      driveStore.currentCase.photosAmount = iCase.photosAmount;
      driveStore.currentCase.filesAmount = iCase.filesAmount;

      if (iCase.owner.accountId !== currentUser.value) {
        router.push(`${iCase.externalUrl}`);
      } else {
        router.push(`/drive/case/my/${iCase.id}/view/`);
      }
    }

    async function fetchCacheData() {
      if (isPublicCases.value) {
        return driveStore.fetchPublicCases();
      }
      if (!isPublicCases.value && currentPage.value === NAVIGATION_PAGES.MY_PUBLIC_CASES) {
        return driveStore.fetchUserPublicCases(mainStore.user.accountId);
      }
      return driveStore.fetchUserPublicCases(route.params.id as any);
    }

    async function fetchSearchData() {
      const currentSearch = driveStore.search;
      const resolveRequest = async () => {
        if (!isPublicCases.value && currentPage.value === NAVIGATION_PAGES.MY_PUBLIC_CASES) {
          return DriveService.getUserPublicCases(pageIdx.value, driveStore.search, mainStore.user.accountId);
        }
        if (!isPublicCases.value && currentPage.value === NAVIGATION_PAGES.USER_PUBLIC_CASES) {
          return DriveService.getUserPublicCases(pageIdx.value, driveStore.search, route.params.id as any);
        }
        return DriveService.getPublicCases(pageIdx.value, driveStore.search);
      };

      return resolveRequest().then((response) => {
        if (currentSearch !== driveStore.search) {
          return Promise.reject(new Error('response is out of date'));
        }
        if (pageIdx.value === 0) {
          driveStore.searchCases = response.results.data.items;
        } else {
          driveStore.searchCases.push(...response.results.data.items);
        }
        return response;
      });
    }

    function refreshCases() {
      if (isPublicCases.value) {
        driveStore.publicCases.page = 0;
        driveStore.publicCases.lastPage = -1;
      } else {
        driveStore.userPublicCases.page = 0;
        driveStore.userPublicCases.lastPage = -1;
      }

      return fetchCacheData();
    }

    const {
      loadingPull, loosingText, successText, pullingText, loadingText, onRefresh,
    } = UsePullToRefresh(refreshCases);

    function handleScroll() {
      if (loadingPull.value) {
        return;
      }
      if (driveStore.search) {
        onScroll(fetchSearchData, DIRECTION.BOTTOM, dataLoading.value);
      } else {
        onCacheScroll(fetchCacheData, DIRECTION.BOTTOM, casesLastPage.value, casesCurrentPage.value);
      }
    }

    let timeout: ReturnType<typeof setTimeout>;
    watch(() => driveStore.search, (newSearch, oldSearch) => {
      clearTimeout(timeout);
      pageIdx.value = 0;
      dataLoading.value = !!newSearch;
      timeout = setTimeout(() => {
        fetchSearchData()
          .then((response) => {
            setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
          })
          .finally(() => {
            dataLoading.value = false;
          });
      }, 1000);
    });

    function deselectAll(items: ICase[]): ICase[] {
      return items.map((item) => ({
        ...item,
        selected: false,
      }));
    }

    watch(() => driveStore.isCaseSelector, (newVal, prevVal) => {
      if (prevVal && !newVal) {
        driveStore.publicCases.data = deselectAll(driveStore.publicCases.data);
        driveStore.searchCases = deselectAll(driveStore.searchCases);
      }
    });

    onMounted(() => {
      driveStore.search = '';
      MonitoringService.sendPageMountedSpeed();
    });

    onBeforeUnmount(() => {
      driveStore.publicCases.page = 0;
    });

    return {
      cases,
      dataLoading,
      mainStore,
      driveStore,
      router,
      isPageLoading,
      scroller,
      noCasesMessage,
      isPublicCases,
      handleScroll,
      onCaseClick,

      loadingPull,
      loosingText,
      successText,
      pullingText,
      loadingText,
      onRefresh,
    };
  },
});
